import { BookOpenCheck, Search } from 'lucide-react-native';
import { useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import { KeyboardStickyView } from 'react-native-keyboard-controller';

import { CitationRequestReviewBody } from './CitationRequestReviewBody';
import { CitationRequestReviewFooter } from './CitationRequestReviewFooter';
import { useCitationRequestReviewSheet } from '../../hooks/useCitationRequestReviewSheet';
import type { CitationRequest } from '../../model/citationRequest';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementReferenceHelpRequest,
} from '../../model/extractedStatements';
import type { StatementTriagePhase } from '../../state/statementTriageStore';

import { HStack } from '@/components/ui/hstack';
import { LoaderButton } from '@/components/ui/loader-button';
import { WizardSteps, type WizardStep } from '@/components/wizard/WizardSteps';
import { useHaptic } from '@/hooks/useHaptic';
import { useToaster } from '@/hooks/useToaster';

/**
 * Title shown in the triage sheet header. Phase-specific copy keeps
 * the wizard's stage explicit even for screen reader users who skip
 * past the step indicator.
 */
export function pickPhaseTitle(phase: StatementTriagePhase): string {
  if (phase === 'review') {
    return 'Review references';
  }

  if (phase === 'provide') {
    return 'Add references';
  }

  // `selection` is now only the brief auto-start launch state (the
  // statement picker is gone) — it shares the search title so the
  // header reads correctly while the search kicks off.
  return 'Searching for references';
}

const CITATION_WIZARD_STEPS: readonly WizardStep<'searching' | 'review'>[] = [
  { key: 'searching', label: 'Search', icon: Search },
  { key: 'review', label: 'Review', icon: BookOpenCheck },
];

/**
 * The search flow's two steps. `selection` is the brief auto-start launch
 * state and has no step of its own, so it shows under Search.
 */
export function CitationWizardSteps({ phase }: { phase: StatementTriagePhase }) {
  const current = phase === 'review' ? 'review' : 'searching';

  return <WizardSteps steps={CITATION_WIZARD_STEPS} current={current} />;
}

interface InlineReviewPhaseProps {
  conversationId: string;
  citationRequest: CitationRequest;
  extractedStatements: readonly ExtractedStatement[];
  attachedStatementReferences: readonly AttachedStatementReference[];
  /** Currently-persisted per-statement free-text help requests on the
   * parent message. Threaded into the review body so each statement
   * group can render its `StatementReferenceHelpRequestRow`. Pass
   * `undefined` (the default for messages with no help requests yet)
   * and the rows still render in the input-panel state. */
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
  /** Called once a successful publish round-trip completes (isPublishing
   * went true → false with no error). The wizard route uses this to
   * `router.dismiss()` so the expert lands back on the chat after their
   * references are applied. Optional so the Actionsheet/Modal variants
   * — which manage their own close — can keep the existing behavior. */
  onPublishSuccess?: () => void;
  /** When provided, fires whenever the publish CTA state changes so a
   * parent can render Publish in its own chrome (e.g. the wizard's
   * route header). The phase still owns the publish state; the
   * callback just lets the parent mirror it. Callers that pass this
   * almost always also pass `hideStickyFooter` to suppress the
   * in-phase footer and avoid double-rendering the CTA. */
  onPublishCtaChange?: (cta: HeaderPublishCta | null) => void;
  /** Suppress the in-phase sticky footer (publish CTA bar). Use when
   * the parent is rendering Publish elsewhere (e.g. in a route header
   * via `onPublishCtaChange`). */
  hideStickyFooter?: boolean;
}

/** Snapshot of the publish CTA that parents can render outside the
 * phase (e.g. in a route header). Mirrors the props the in-phase
 * footer's LoaderButton needs to render the same button elsewhere. */
export interface HeaderPublishCta {
  label: string;
  isDisabled: boolean;
  isPublishing: boolean;
  onPress: () => void;
}

/**
 * Review phase rendered inside the triage actionsheet/modal. The
 * wizard is the only review surface — the chat-bubble pill opens this
 * same overlay for both fresh selection→search→review flows and for
 * pill-into-review reopens (`awaiting_review` / `published`). All
 * review-specific state (approve set, publish lifecycle, attached
 * snapshot) lives in `useCitationRequestReviewSheet`, which is shared
 * with no other surface today.
 */
export function InlineReviewPhase({
  conversationId,
  citationRequest,
  extractedStatements,
  attachedStatementReferences,
  statementReferenceHelpRequests,
  onPublishSuccess,
  onPublishCtaChange,
  hideStickyFooter,
}: InlineReviewPhaseProps) {
  const {
    isPublishing,
    errorMessage,
    hasCandidates,
    hasUnpublishedChanges,
    publishCount,
    publishCta,
    isDoneState,
    handleDismiss,
    handleToggle,
    isApproved,
  } = useCitationRequestReviewSheet({
    conversationId,
    citationRequest,
    currentAttachedStatementReferences: attachedStatementReferences,
    currentStatementReferenceHelpRequests: statementReferenceHelpRequests,
    extractedStatements,
  });

  const toast = useToaster();

  const haptic = useHaptic();

  /**
   * Snapshot of the publish count at the moment the user tapped
   * Publish. Read at success time to compose the confirmation toast
   * because by then the store may have synced new attached refs and
   * the live count no longer reflects what was just published.
   */
  const publishCountAtPressRef = useRef(0);

  /**
   * One-shot guard ensuring `onPublishSuccess` fires exactly once per
   * publish lifecycle regardless of which code path triggers it (the
   * inline `isDoneState` fast-path or the `isPublishing` watcher
   * below). Reset whenever a new publish begins so each lifecycle
   * gets its own gate.
   */
  const successFiredRef = useRef(false);

  /**
   * Wrap the publish CTA so the "Done" no-diff case also fires
   * `onPublishSuccess`. The publish handler skips the network call
   * when there's nothing new to publish, so `isPublishing` never
   * transitions and the effect below won't trigger — we have to
   * dismiss inline. For real publishes we still let the effect
   * watch the transition so errors keep the wizard open.
   */
  const wrappedPublishCta = useMemo(
    () => ({
      ...publishCta,
      onPress: () => {
        publishCountAtPressRef.current = publishCount;

        // Re-arm the one-shot success guard: a fresh publish lifecycle is
        // starting, so a second publish in the same mounted phase can fire
        // onPublishSuccess again (the watcher below otherwise stays blocked
        // by a guard that was never reset).
        successFiredRef.current = false;

        publishCta.onPress();

        if (isDoneState) {
          successFiredRef.current = true;

          onPublishSuccess?.();
        }
      },
    }),
    [publishCta, publishCount, isDoneState, onPublishSuccess]
  );

  /**
   * Fire `onPublishSuccess` once the publish round-trip completes
   * cleanly — `isPublishing` transitions true → false with no error.
   * The wizard route uses this to dismiss itself so the expert lands
   * back on the chat after their references are applied. We watch
   * the transition rather than the publish handler itself so the
   * dismiss only fires on success (errors leave `errorMessage` set
   * and the wizard stays open for the expert to retry).
   */
  const wasPublishingRef = useRef(isPublishing);

  useEffect(() => {
    if (wasPublishingRef.current && !isPublishing && !errorMessage && !successFiredRef.current) {
      successFiredRef.current = true;

      const count = publishCountAtPressRef.current;

      if (count > 0) {
        haptic.success();

        toast.showSuccess({
          title: `You published ${count} ${count === 1 ? 'reference' : 'references'}!`,
        });
      }

      onPublishSuccess?.();
    }

    wasPublishingRef.current = isPublishing;
  }, [isPublishing, errorMessage, onPublishSuccess, haptic, toast]);

  /**
   * Mirror the publish CTA up to a parent that wants to render Publish
   * in its own chrome (the wizard's route header today). The phase
   * still owns the CTA's behavior — we just expose a snapshot.
   *
   * When there are candidates but no diff, the button stays visible
   * but disabled — matches the iOS Mail/Reminders pattern where the
   * primary action (Send/Add) stays pinned top-right and toggles
   * disabled until the input is valid, so the user learns the button's
   * location once and it never shifts under them.
   *
   * Clears on unmount so a stale Publish button can't linger in the
   * parent after the phase tears down.
   */
  useEffect(() => {
    if (!onPublishCtaChange) {
      return;
    }

    if (!hasCandidates) {
      onPublishCtaChange(null);

      return;
    }

    onPublishCtaChange({
      label: wrappedPublishCta.label,
      isDisabled: wrappedPublishCta.isDisabled || !hasUnpublishedChanges,
      isPublishing,
      onPress: wrappedPublishCta.onPress,
    });
  }, [onPublishCtaChange, wrappedPublishCta, hasCandidates, hasUnpublishedChanges, isPublishing]);

  useEffect(() => {
    if (!onPublishCtaChange) {
      return;
    }

    return () => onPublishCtaChange(null);
  }, [onPublishCtaChange]);

  /**
   * Publish row at the bottom of the references scroll. Mirrors the
   * header Publish CTA so the primary action is also reachable at the
   * end of the list. Rendered inside the scroll content so it scrolls
   * into view at the end of the references list rather than sitting in
   * a sticky bar.
   */
  const bottomActionsSlot = hasCandidates ? (
    <HStack className="w-full flex-nowrap items-center gap-2 px-4 pb-6 pt-3">
      <LoaderButton
        onPress={wrappedPublishCta.onPress}
        action="primary"
        variant="solid"
        size="md"
        isLoading={isPublishing}
        isDisabled={wrappedPublishCta.isDisabled || !hasUnpublishedChanges}
        className="ml-auto"
        aria-label={
          wrappedPublishCta.isDisabled || !hasUnpublishedChanges
            ? 'Publish — no changes to publish'
            : wrappedPublishCta.label
        }>
        {wrappedPublishCta.label}
      </LoaderButton>
    </HStack>
  ) : null;

  return (
    <View style={{ flex: 1 }}>
      {/* Body fills the entire phase area. The list inside applies
          its own bottom inset via `scrollBottomInset` so content can
          scroll *under* the sticky footer — making the fade visually
          meaningful as items disappear behind the bar. */}
      <View style={{ flex: 1 }}>
        <CitationRequestReviewBody
          citationRequest={citationRequest}
          extractedStatements={extractedStatements}
          isPublishing={isPublishing}
          errorMessage={errorMessage}
          hasCandidates={hasCandidates}
          isApproved={isApproved}
          onToggle={handleToggle}
          publishCta={wrappedPublishCta}
          handleDismiss={handleDismiss}
          helpRequestContext={{
            statementReferenceHelpRequests,
          }}
          scrollBottomInset={hideStickyFooter ? undefined : GLASS_FOOTER_RESERVED_SPACE}
          endSlot={hideStickyFooter ? bottomActionsSlot : null}
        />
      </View>

      {/* In-phase sticky footer. Hidden when the parent wants to host
          Publish in its own chrome (`hideStickyFooter`) — the wizard
          route does this so Publish lives in the header above the
          keyboard. Actionsheet/Modal variants keep the footer because
          their host chrome doesn't have a header right slot.
          KeyboardStickyView keeps the bar above the keyboard. The
          scroll list's `bottomOffset` (80px) is sized to clear the
          bar so the focused help-request input always scrolls above
          both the keyboard and the bar. */}
      {hideStickyFooter ? null : (
        <KeyboardStickyView offset={{ closed: 0, opened: 0 }}>
          <View className="border-t border-outline-100 bg-background-0" style={{ zIndex: 10 }}>
            <CitationRequestReviewFooter
              publishCta={wrappedPublishCta}
              isPublishing={isPublishing}
              hasCandidates={hasCandidates}
              className="w-full flex-nowrap items-center gap-2 px-4 pb-2 pt-2"
            />
          </View>
        </KeyboardStickyView>
      )}
    </View>
  );
}

/** Space reserved at the bottom of the body for the action bar so the
 * last list item can scroll above the bar instead of sitting under
 * it. Sized to clear the compact footer (~52px = button row +
 * `pb-3 pt-2`) plus a small buffer. */
const GLASS_FOOTER_RESERVED_SPACE = 64;
